// Referral-source taxonomy — the SINGLE source of truth for "where did this
// business come from?" vocabulary across Stewardship OS.
//
// CONSUMER: shared (Workers runtime + web via @shared + tests). PURE and
// dependency-free — no React, no DB client — so every surface can import it:
//   • the filter registry (shared/filters/field-registry.ts) — contact + opp
//     source filters,
//   • the opportunity source column (shared/db/opportunities.ts + Phase 1
//     migration 0209),
//   • the contact + intake capture UIs (Phase 2),
//   • the Referral Network reporting rollups (Phase 4).
//
// WHY ONE LIST (Grant, 2026-07-18): source attribution was drifting — the
// filter registry hardcoded one option list, the contact form hinted another,
// and opportunities had no source at all. Everything now reads THIS list, so
// the vocabulary is edited in exactly one place.
//
// STORED vs LABEL: `value` is the stored enum string and is STABLE — the
// 2026-07-18 review relabeled three options for clarity WITHOUT changing their
// stored values, so historical rows and existing saved filters keep working
// and no data-rewrite migration is needed. Only the display `label` changed.
// New values (marketing/content channels) store their label verbatim.
//
// "Existing Client" added 2026-08-13 (Ryan, video feedback): "Current
// Client" / "Past Client" mean someone ELSE referred this person — they
// don't cover the household itself already being a client picking up
// another product (his example: Monica, an existing client adding a new
// service). AC had this as its own value ("Existing Client"); this restores
// it. It's a distinct rollup category (`existing`), not folded into
// `person`, because repeat business from someone we already serve is a
// different story than a stranger being referred in by someone else.

/** The six rollup buckets the Referral Network page groups sources into. */
export const REFERRAL_SOURCE_CATEGORIES = {
  person: "Referred by a person",
  partner: "Referral partner",
  internal: "Internal cross-sell",
  existing: "Existing client — repeat business",
  marketing: "Marketing / content",
  unknown: "Unknown",
} as const;

export type ReferralSourceCategory = keyof typeof REFERRAL_SOURCE_CATEGORIES;

export interface ReferralSourceOption {
  /** Stored enum value — STABLE across label renames. */
  value: string;
  /** Display label shown in dropdowns, chips, and reports. */
  label: string;
  /** Rollup bucket for reporting. */
  category: ReferralSourceCategory;
}

// Ordered by category (A→F) so a dropdown reads top-to-bottom the way the
// taxonomy is grouped. `value` renames are display-only (see header).
export const REFERRAL_SOURCE_OPTIONS: readonly ReferralSourceOption[] = [
  // A. Referred by a person (word-of-mouth). Labels reworded 2026-08-13 to
  // "Referred by a ___ Client" — the bare "Current Client" / "Past Client"
  // read as a description of the PERSON THEMSELVES (which is what confused
  // Ryan), when they actually mean "a current/past client referred this
  // person to us." Stored values unchanged.
  { value: "Current Client", label: "Referred by a Current Client", category: "person" },
  { value: "Past Client", label: "Referred by a Past Client", category: "person" },
  // Relabel of stored "Family/Friend" → clarifies it means a team member's.
  { value: "Family/Friend", label: "Family/Friend of Team Member", category: "person" },
  // Relabel of stored "Personal Relationship" → the advisor's own network.
  { value: "Personal Relationship", label: "Advisor's Own Network", category: "person" },

  // B. Referral partner (business/professional)
  { value: "Referral Partner", label: "Referral Partner", category: "partner" },
  // The Ambassador Agent program (0241/0242/0244). Its own value rather
  // than a flavour of "Referral Partner" because it is the only source
  // that carries a PAYOUT: picking it names a specific 1099 contractor
  // who is owed a fee, so it must be distinguishable in reporting and
  // must demand the companion field below. See AMBASSADOR_SOURCE_VALUE.
  { value: "Ambassador Agent", label: "Ambassador Agent", category: "partner" },
  { value: "Dave Ramsey ELP", label: "Dave Ramsey ELP", category: "partner" },
  { value: "Realtor.com", label: "Realtor.com", category: "partner" },
  // Realty's paid Facebook lead-gen vendor (Nate, 2026-07-24: "a real estate
  // ad company we pay to run Facebook ads for us — pretty much all of their
  // leads are from Facebook"). A named paid vendor, not a generic channel —
  // parallels Dave Ramsey ELP / Realtor.com rather than the generic "Social
  // Media" / "Paid Ad" marketing options below.
  { value: "Platform Marketing", label: "Platform Marketing (Facebook Ads)", category: "partner" },

  // C. Internal cross-sell — ALWAYS paired with a from-company (see
  // CROSS_COMPANY_SOURCE_VALUE). Relabel of stored "Cross-Company".
  { value: "Cross-Company", label: "Cross-Company (Internal)", category: "internal" },

  // D. Existing client, same company, no referral involved — the household
  // is already a client here and is picking up another product (Ryan's
  // Monica example). NEW 2026-08-13; restores AC's "Existing Client" value.
  // Auto-stamped by createOpportunity when this is plainly true (see
  // shared/db/opportunities.ts); an advisor's explicit pick always wins.
  { value: "Existing Client", label: "Existing Client (repeat business)", category: "existing" },

  // E. Stewardship-driven marketing / content (NEW channels, 2026-07-18)
  {
    value: "Stewardship Productions / Content",
    label: "Stewardship Productions / Content",
    category: "marketing",
  },
  { value: "Website Form", label: "Website Form", category: "marketing" },
  { value: "Event/Webinar", label: "Event/Webinar", category: "marketing" },
  { value: "Online Search / SEO", label: "Online Search / SEO", category: "marketing" },
  { value: "Paid Ad", label: "Paid Ad", category: "marketing" },
  { value: "Social Media", label: "Social Media", category: "marketing" },
  // Realty's Google Local Services ad (Marketing Hub row 9b.4): a paid lead
  // source the OS stamps on the opportunity it opens for each lead. Never a
  // referral (decision 32): it is not "Referral Partner", so no referral row
  // is written for it.
  // DECISION (slice 9b audit, finding 2; Grant 2026-10-23): marketing, not
  // partner. Both are ads Stewardship runs itself, not a business sending
  // people; filed as partner, the Referral Network grouped them with partner
  // referrals and the influence ledger read a lead as "someone referred them".
  { value: "Google Local Services", label: "Google Local Services", category: "marketing" },
  // Row 9b.6: a Facebook or Instagram lead ad's form, the paid lead source the
  // OS stamps on the opportunity it opens for each lead. Never a referral
  // (decision 32).
  { value: "Meta lead ad", label: "Meta lead ad", category: "marketing" },

  // F. Unknown / catch-all
  { value: "Cold/Unknown", label: "Cold/Unknown", category: "unknown" },
  { value: "Other", label: "Other", category: "unknown" },
];

/** Stored values only, in taxonomy order — for validation + enum-ish checks. */
export const REFERRAL_SOURCE_VALUES: readonly string[] =
  REFERRAL_SOURCE_OPTIONS.map((o) => o.value);

// The one source value that is meaningless without a from-company: an internal
// cross-sell MUST record WHICH Stewardship company made the referral. The
// create path stamps it automatically on cross-sells and rejects it when the
// from-company is missing (see shared/db/opportunities.ts).
export const CROSS_COMPANY_SOURCE_VALUE = "Cross-Company";

// The second source value that is meaningless on its own: an ambassador
// referral MUST name WHICH ambassador, because that person is owed a
// referral fee. "An ambassador sent them" is not attribution — it is an
// unpayable IOU addressed to nobody.
//
// The companion field is the EXISTING internal_referrer_user_id column
// on both contacts and opportunities. Reused rather than duplicated:
// ambassadors are employee-typed users, and the ambassador's own intake
// form has stamped that same column since 0242, so form-submitted and
// advisor-designated referrals land in one shape.
//
// createOpportunity enforces the pairing (both directions: the id is
// required when this source is picked, and it must belong to somebody
// who actually holds the ambassador role).
export const AMBASSADOR_SOURCE_VALUE = "Ambassador Agent";

// The source value createOpportunity auto-stamps when a new opportunity is
// created for a household that's already an active client of the SAME
// company (Ryan's Monica example) and the advisor didn't pick a source. See
// shared/db/opportunities.ts.
export const EXISTING_CLIENT_SOURCE_VALUE = "Existing Client";

/** Display label for a stored value; falls back to the raw value if unknown. */
export function referralSourceLabel(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return REFERRAL_SOURCE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/** Rollup category for a stored value; null when unknown/absent. */
export function referralSourceCategory(
  value: string | null | undefined,
): ReferralSourceCategory | null {
  if (!value) return null;
  return (
    REFERRAL_SOURCE_OPTIONS.find((o) => o.value === value)?.category ?? null
  );
}

/** True when this source is a valid stored value. */
export function isKnownReferralSource(value: string | null | undefined): boolean {
  return !!value && REFERRAL_SOURCE_VALUES.includes(value);
}
